import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import AirportSelectDropdown from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/AirportSelectDropdown'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ToggleButton from 'LuxKit/ToggleButton'
import IconButton from 'LuxKit/Button/IconButton'
import InputChip from 'LuxKit/Chips/InputChip'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import useToggle from 'hooks/useToggle'
import useExperiences from 'hooks/Experiences/useExperiences'
import useExperiencesBatched from 'hooks/Experiences/useExperiencesBatched'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoungePassesRegionGroup from './LoungePassesRegionGroup'
import { LoungePassesExperienceTile } from './LoungePassesLoungeTile'
import { LOUNGE_DIRECTORY_ID } from './LoungePassesHeroSection'
import {
  REGION_DISPLAY_NAMES,
  REGION_DISPLAY_ORDER,
  type LoungeRegion,
} from './loungePassesConstants'
import AIRPORT_LOUNGE_INDEX from './loungeData/airportLoungeIndex'
import CONTINENT_AIRPORT_INDEX from './loungeData/continentAirportIndex'
import { AIRPORT_CODE_LOUNGE_INDEX } from './loungeData/loungeAirportLookup'
import getFeaturedLoungeIdsByRegion from './loungeData/getFeaturedLoungeIdsByRegion'
import LoungePassesRegionFilterDropdown from './LoungePassesRegionFilterDropdown'

const Section = styled.div`
  padding: ${rem(40)} ${rem(4)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} 0 0;
  }
`

const SearchContainer = styled.div`
  max-width: ${rem(500)};
  margin-inline: auto;
  padding-bottom: ${rem(20)};
  position: relative;
`

const FilterChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const TilesGrid = styled.div`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const FEATURED_BY_REGION = getFeaturedLoungeIdsByRegion()
const ALL_FEATURED_IDS = Object.values(FEATURED_BY_REGION).flat()

type SelectedCategory = LoungeRegion | 'all'

function getLoungeIdsForCountries(region: LoungeRegion, countryCodes: Set<string>): Array<string> {
  const countries = CONTINENT_AIRPORT_INDEX[region]
  const loungeIds: Array<string> = []
  for (const country of countries) {
    if (countryCodes.has(country.key)) {
      for (const airport of country.airports) {
        const ids = AIRPORT_LOUNGE_INDEX[airport.id] ?? []
        loungeIds.push(...ids)
      }
    }
  }
  return loungeIds
}

function LoungePassesDirectorySection() {
  const intl = useIntl()
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>('all')
  const [selectedCountries, setSelectedCountries] = useState<Map<LoungeRegion, Set<string>>>(new Map())
  const [searchPhrase, setSearchPhrase] = useState('')
  const [selectedAirport, setSelectedAirport] = useState<App.AirportLocation | undefined>()
  const [searchAirportCodes, setSearchAirportCodes] = useState<Array<string>>([])
  const [dropdownOpen, , openDropdown, closeDropdown] = useToggle()
  const inputRef = useRef<HTMLInputElement>(null)

  const [airports, airportsLoading] = useAirportSearch(searchPhrase)
  const popularAirports = usePopularAirports('departure')
  const showSearch = !!(searchPhrase && searchPhrase.length > 2)
  const airportList = showSearch ? airports : popularAirports
  const airportListRef = useRef(airportList)
  airportListRef.current = airportList

  // Lounge IDs for search-selected airport
  const searchLoungeIds = useMemo(
    () => searchAirportCodes.flatMap(code => AIRPORT_CODE_LOUNGE_INDEX[code] ?? []),
    [searchAirportCodes],
  )

  const [searchLoungeExperiences, searchLoungesLoading] = useExperiences(searchLoungeIds, { showUnlisted: true })

  // Lounge IDs from country filter selections
  const filteredLoungeIds = useMemo(() => {
    const ids: Array<string> = []
    for (const [region, countries] of selectedCountries) {
      if (countries.size > 0) {
        ids.push(...getLoungeIdsForCountries(region, countries))
      }
    }
    return ids
  }, [selectedCountries])

  const hasCountryFilters = filteredLoungeIds.length > 0

  // Featured lounges for the default directory view
  const [featuredExperiences, featuredLoading] = useExperiences(
    hasCountryFilters ? [] : ALL_FEATURED_IDS,
    { showUnlisted: true },
  )

  // Filtered lounges when country filters are applied (batched to avoid URL length limits)
  const [filteredExperiences, filteredLoading] = useExperiencesBatched(
    hasCountryFilters ? filteredLoungeIds : [],
    { showUnlisted: true },
  )

  const experiencesByRegion = useMemo(() => {
    const experiences = hasCountryFilters ? filteredExperiences : featuredExperiences
    const expMap = new Map(experiences.map(e => [e.id, e]))
    const regionIdPairs: Array<[LoungeRegion, Array<string>]> = hasCountryFilters
      ? [...selectedCountries.entries()]
          .filter(([, countries]) => countries.size > 0)
          .map(([region, countries]) => [region, getLoungeIdsForCountries(region, countries)])
      : REGION_DISPLAY_ORDER.map(region => [region, FEATURED_BY_REGION[region]])

    const result = new Map<LoungeRegion, Array<App.ExperienceOffer>>()
    for (const [region, ids] of regionIdPairs) {
      const exps = ids.map(id => expMap.get(id)).filter((e): e is App.ExperienceOffer => !!e)
      if (exps.length > 0) result.set(region, exps)
    }
    return result
  }, [hasCountryFilters, featuredExperiences, filteredExperiences, selectedCountries])

  const visibleRegions = useMemo(() => {
    if (hasCountryFilters || selectedCategory === 'all') {
      return REGION_DISPLAY_ORDER.filter(region => experiencesByRegion.has(region))
    }
    return [selectedCategory].filter(region => experiencesByRegion.has(region))
  }, [selectedCategory, experiencesByRegion, hasCountryFilters])

  // Build filter chip data from applied country filters
  const filterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; region: LoungeRegion; country?: string }> = []
    for (const [region, countries] of selectedCountries) {
      if (countries.size === 0) continue
      const regionCountries = CONTINENT_AIRPORT_INDEX[region]
      const allSelected = regionCountries.length === countries.size
      if (allSelected) {
        chips.push({
          key: `all-${region}`,
          label: intl.formatMessage(
            { id: 'zzCHh9oMCKsgzbxRgbAP', defaultMessage: 'All {regionName}', description: 'Checkbox label to select all countries in a lounge region filter' },
            { regionName: REGION_DISPLAY_NAMES[region] },
          ),
          region,
        })
      } else {
        for (const c of regionCountries) {
          if (countries.has(c.key)) {
            chips.push({ key: `${region}-${c.key}`, label: c.label, region, country: c.key })
          }
        }
      }
    }
    return chips
  }, [selectedCountries, intl])

  const handleRemoveChip = useCallback((region: LoungeRegion, country?: string) => {
    setSelectedCountries(prev => {
      const next = new Map(prev)
      if (!country) {
        next.delete(region)
      } else {
        const updated = new Set(next.get(region))
        updated.delete(country)
        updated.size === 0 ? next.delete(region) : next.set(region, updated)
      }
      if (next.size === 0) setSelectedCategory('all')
      return next
    })
  }, [])

  const handleCategoryClick = useCallback((category: SelectedCategory) => {
    setSelectedCategory(category)
    if (category === 'all') setSelectedCountries(new Map())
  }, [])

  const handleViewAllRegion = useCallback((region: LoungeRegion) => {
    setSelectedCountries(prev => new Map(prev).set(region, new Set(CONTINENT_AIRPORT_INDEX[region].map(c => c.key))))
    setSelectedCategory(region)
  }, [])

  const handleRegionFilterApply = useCallback((region: LoungeRegion, countries: Set<string>) => {
    setSelectedCountries(prev => {
      const next = new Map(prev)
      countries.size === 0 ? next.delete(region) : next.set(region, countries)
      return next
    })
    setSelectedCategory(countries.size > 0 ? region : 'all')
  }, [])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.currentTarget.value)
    setSelectedAirport(undefined)
    setSearchAirportCodes([])
  }, [])

  const handleSelectAirport = useCallback((airport: App.AirportLocation) => {
    setSelectedAirport(airport)
    if (airport.isMultiAirport && airport.cityCode) {
      setSearchAirportCodes(
        airportListRef.current
          .filter(a => !a.isMultiAirport && a.cityCode === airport.cityCode)
          .map(a => a.airportCode),
      )
    } else {
      setSearchAirportCodes([airport.airportCode])
    }
    setSearchPhrase('')
    closeDropdown()
    inputRef.current?.blur()
  }, [closeDropdown])

  const handleClearSearch = useCallback(() => {
    setSelectedAirport(undefined)
    setSearchAirportCodes([])
    setSearchPhrase('')
  }, [])

  const searchPlaceholder = intl.formatMessage({
    id: 'Ro4BzzJbJ14ooKrMfohb',
    defaultMessage: 'Search by airport, city or country',
    description: 'Search input placeholder text for the lounge directory search field',
  })

  const popularAirportsHeading = intl.formatMessage({
    id: 'VgLkOlXdFP7JO5mtl6fP',
    defaultMessage: 'Popular airports',
    description: 'Heading for the popular airports section shown in the lounge search dropdown',
  })

  const allLoungesLabel = intl.formatMessage({
    id: 'yK-pbB5Y3VirG3YDdYMM',
    defaultMessage: 'All Lounges',
    description: 'Toggle button label for resetting filter to show all lounges',
  })

  const displayValue = getAirportDisplayText(selectedAirport)
  const hasSelectedAirport = !!selectedAirport
  const showSearchResults = hasSelectedAirport
  const isLoading = hasCountryFilters ? filteredLoading : featuredLoading

  return <Section id={LOUNGE_DIRECTORY_ID}>
    <LayoutContainer variant="large">
      <VerticalSpacer gap={32}>
        <VerticalSpacer gap={16}>
          <Heading variant="heading2" align="center">
            {intl.formatMessage({
              id: 'AzgyqYqO2C2itqfqNcMO',
              defaultMessage: 'Available airport lounges',
              description: 'Heading for the available airport lounges directory section',
            })}
          </Heading>
          <SearchContainer>
            <TextInputOnly
              placeholder={searchPlaceholder}
              endIcon={hasSelectedAirport && !dropdownOpen
                ? <IconButton kind="tertiary" variant="dark" size="small" onClick={handleClearSearch}><LineTimesIcon /></IconButton>
                : <LineSearchIcon />}
              value={dropdownOpen ? searchPhrase : (displayValue ?? '')}
              onChange={handleSearchChange}
              onFocus={openDropdown}
              ref={inputRef}
              noValidationSpacing
            />
            <AirportSelectDropdown
              open={dropdownOpen}
              loading={airportsLoading}
              onClose={closeDropdown}
              onSelectAirport={handleSelectAirport}
              airports={airportList}
              inputValue={searchPhrase}
              anchorRef={inputRef}
              triggerRef={inputRef}
              heading={showSearch ? undefined : popularAirportsHeading}
            />
          </SearchContainer>
        </VerticalSpacer>
        {!showSearchResults && <>
          <HorizontalContainerWithFadedOverflow gap={12}>
            <ToggleButton
              kind="secondary"
              orientation="horizontal"
              isSelected={selectedCategory === 'all'}
              title={allLoungesLabel}
              onClick={() => handleCategoryClick('all')}
            />
            {REGION_DISPLAY_ORDER.map(region => (
              <LoungePassesRegionFilterDropdown
                key={region}
                region={region}
                regionName={REGION_DISPLAY_NAMES[region]}
                isSelected={selectedCategory === region}
                selectedCountries={selectedCountries.get(region) ?? new Set()}
                onApply={handleRegionFilterApply}
              />
            ))}
          </HorizontalContainerWithFadedOverflow>
          {hasCountryFilters && filterChips.length > 0 && <FilterChipsRow>
            {filterChips.map(chip => (
              <InputChip
                key={chip.key}
                variant="filled"
                onClick={() => handleRemoveChip(chip.region, chip.country)}
              >
                {chip.label}
              </InputChip>
            ))}
          </FilterChipsRow>}
          {isLoading && <LoadingSpinner />}
          {!isLoading && hasCountryFilters && <TilesGrid>
            {visibleRegions.flatMap(region =>
              (experiencesByRegion.get(region) ?? []).map(experience => (
                <LoungePassesExperienceTile key={experience.id} experience={experience} />
              )),
            )}
          </TilesGrid>}
          {!isLoading && !hasCountryFilters && <VerticalSpacer gap={40}>
            {visibleRegions.map(region => (
              <LoungePassesRegionGroup
                key={region}
                regionName={REGION_DISPLAY_NAMES[region]}
                experiences={experiencesByRegion.get(region) ?? []}
                onViewAll={() => handleViewAllRegion(region)}
              />
            ))}
          </VerticalSpacer>}
        </>}
        {showSearchResults && <VerticalSpacer gap={16}>
          {searchLoungesLoading && <LoadingSpinner />}
          {!searchLoungesLoading && searchLoungeExperiences.length === 0 && <div>
            {intl.formatMessage({
              id: 'cCQVtYxNEZZaWB-crF1U',
              defaultMessage: 'No lounges found for this airport',
              description: 'Message when no lounges are available for the selected airport',
            })}
          </div>}
          {!searchLoungesLoading && searchLoungeExperiences.length > 0 && <TilesGrid>
            {searchLoungeExperiences.map(experience => (
              <LoungePassesExperienceTile key={experience.id} experience={experience} />
            ))}
          </TilesGrid>}
        </VerticalSpacer>}
      </VerticalSpacer>
    </LayoutContainer>
  </Section>
}

export default LoungePassesDirectorySection
